import { Color, Spacing, Typography } from '@persona-id/design-tokens';
import { useState } from 'react';
import { BORDER_RADIUSES } from 'shared/styles/variables';
import styled from 'styled-components';

import JsonSchemaSelect, { formatDisplayValue } from 'dashboard/components/JsonSchemaSelect';
import ParamLabel from 'dashboard/components/WorkflowControlPanel/ParamLabel';
import { StepAdvancedConfigurationWithErrorHandlingPanel } from 'dashboard/components/WorkflowControlPanel/StepAdvancedConfigurationWithErrorHandlingPanel';
import useWorkflowError from 'dashboard/components/WorkflowControlPanel/useWorkflowError';
import { Trigger } from 'dashboard/models/trigger';
import { WorkflowError } from 'dashboard/models/workflow';
import { WorkflowRunStepLoop } from 'dashboard/models/workflow-step';
import {
  WorkflowStep,
  WorkflowStepLoop,
  WorkflowStepLoopMapSettings,
  WorkflowStepLoopModeType,
} from 'dashboard/models/workflow-version';
import Select, { Option } from 'design-system/components/Select';
import Spacer from 'design-system/components/Spacer';
import { Paragraph } from 'design-system/components/Text';
import useFeatureFlag from 'lib/hooks/useFeatureFlag';
import { JsonSchema, JsonSchemaTypes } from 'lib/json-schema';

const HelperText = styled.div`
  ${Typography.TextSmall};
  line-height: 1;
  color: ${({ theme }) => theme.color.text.secondary};
  margin-top: ${Spacing[2]};
`;

interface MapSettingsProps {
  editable: boolean;
  errors: WorkflowError[];
  jsonSchema: JsonSchema;
  settings: WorkflowStepLoopMapSettings;
  step: WorkflowStepLoop;
  tokensToDisplayValue: Record<string, string>;
  trigger?: Trigger;
  workflowRunStep: WorkflowRunStepLoop;
  updateMapSettings: (arrayPath: string) => void;
  updateMode: (mode: WorkflowStepLoopModeType) => void;
  updateStep: (step: WorkflowStep) => void;
}

const OutputContainer = styled.div`
  background-color: ${Color.CoreSlate50};
  border-radius: ${BORDER_RADIUSES.rounded};
  padding: ${Spacing[2]};
  display: flex;
  flex-direction: column;
`;

const MapSettings = ({
  editable,
  errors,
  jsonSchema,
  settings,
  step,
  tokensToDisplayValue,
  trigger,
  workflowRunStep,
  updateStep,
  updateMode,
  updateMapSettings,
}: MapSettingsProps) => {
  const { hasErrorFor, renderErrorMessageFor } = useWorkflowError<'array-path'>(errors);
  const [inputValue, setInputValue] = useState<string | string[]>(settings['array-path']);
  const maxIterations = useFeatureFlag('global:increase-sequential-loop-max-iterations-limit')
    ? 1000
    : 100;
  return (
    <>
      <ParamLabel labelText='Input' htmlFor='step-config-loop__array-path-input' />
      <JsonSchemaSelect
        id='step-config-loop__array-path'
        allowedTypes={[JsonSchemaTypes.Array]}
        allowedArrayTypes={[
          JsonSchemaTypes.String,
          JsonSchemaTypes.Integer,
          JsonSchemaTypes.Boolean,
          JsonSchemaTypes.Object,
        ]}
        disabled={!editable}
        inputValue={inputValue}
        jsonSchema={jsonSchema}
        tokensToDisplayValue={tokensToDisplayValue}
        onValueChange={setInputValue}
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        onFieldSelect={({ value }) => updateMapSettings(value)}
        size='sm'
        hasError={hasErrorFor('array-path')}
        aria-invalid={hasErrorFor('array-path')}
      />
      <HelperText>Loops that exceed {maxIterations} iterations will error</HelperText>
      {renderErrorMessageFor('array-path')}
      <Spacer height={2} />
      <ParamLabel labelText='Loop Mode' htmlFor='step-config-loop__mode' />
      <Select
        disabled={!editable}
        id='step-config-loop__mode'
        value={settings.mode}
        onValueChange={updateMode}
        placeholder='Select loop mode'
      >
        <Option value={WorkflowStepLoopModeType.SEQUENTIAL}>Sequential</Option>
        <Option value={WorkflowStepLoopModeType.PARALLEL}>Parallel</Option>
      </Select>
      {workflowRunStep && (
        <>
          <Spacer height={2} />
          <ParamLabel labelText='Output array' htmlFor='step-config-loop__output-array' />
          <OutputContainer>
            <Paragraph id='step-config-loop__output-array' size='sm' color='primary'>
              {formatDisplayValue(
                workflowRunStep.attributes['generated-variable']?.variable?.result,
              )}
            </Paragraph>
          </OutputContainer>
        </>
      )}
      <Spacer height={4} />
      <StepAdvancedConfigurationWithErrorHandlingPanel
        step={step}
        trigger={trigger}
        updateStep={updateStep}
        disabled={!editable}
      />
    </>
  );
};

export default MapSettings;
